import React from 'react';
import { Camera, Cpu, Bell, Eye, Shield, CheckCircle, Zap, Smartphone } from 'lucide-react';

/**
 * Section éducative en haut de la page Vidéo Baseline.
 * Explique : (1) avantages caméras Ajax, (2) IA embarquée,
 * (3) synergie avec centrale + sirène Ajax.
 */
const VideoEducationSection = () => {
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-12" data-testid="video-education">
      <div className="text-center mb-10">
        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-purple-500/10 text-purple-300 border border-purple-500/20 mb-3">
          Pourquoi Ajax + IA + écosystème connecté
        </span>
        <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3 tracking-tight">
          Une vidéosurveillance plus intelligente, plus dissuasive
        </h2>
        <p className="text-white/60 max-w-2xl mx-auto text-sm leading-relaxed">
          Les caméras Ajax ne sont pas de simples enregistreurs : couplées à votre centrale et sirène,
          elles deviennent un véritable bouclier connecté avec vérification visuelle, IA embarquée et
          réaction immédiate.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mb-8">
        {/* Caméras Ajax */}
        <div className="bg-gradient-to-b from-purple-500/5 to-transparent border border-purple-500/20 rounded-2xl p-6">
          <div className="w-11 h-11 rounded-lg bg-purple-500/15 flex items-center justify-center mb-4">
            <Camera className="w-5 h-5 text-purple-300" />
          </div>
          <h3 className="text-white font-semibold mb-2">Caméras conçues par Ajax</h3>
          <p className="text-white/60 text-sm mb-4 leading-relaxed">
            Boîtier full-metal, étanchéité IP65, garantie 4 ans, mise à jour OTA. Chaque caméra
            partage le même design soigné et la même app que votre alarme — un seul écosystème, une seule interface.
          </p>
          <ul className="space-y-1.5 text-xs text-white/55">
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-purple-400" /> Résolution 5MP et 8MP (4K)</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-purple-400" /> True WDR pour contre-jour</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-purple-400" /> Chiffrement de bout en bout</li>
          </ul>
        </div>

        {/* IA embarquée */}
        <div className="bg-gradient-to-b from-blue-500/5 to-transparent border border-blue-500/20 rounded-2xl p-6 relative">
          <div className="absolute top-4 right-4 px-2 py-0.5 bg-blue-500/20 text-blue-300 text-[10px] font-bold rounded uppercase">
            Intelligence
          </div>
          <div className="w-11 h-11 rounded-lg bg-blue-500/15 flex items-center justify-center mb-4">
            <Cpu className="w-5 h-5 text-blue-300" />
          </div>
          <h3 className="text-white font-semibold mb-2">IA embarquée dans la caméra</h3>
          <p className="text-white/60 text-sm mb-4 leading-relaxed">
            L'IA tourne directement dans la caméra (pas dans le cloud). Elle distingue les humains,
            les véhicules et les animaux pour éliminer les fausses alertes du quotidien.
          </p>
          <ul className="space-y-1.5 text-xs text-white/55">
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400" /> Détection humains / véhicules</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400" /> Filtrage des animaux et feuillage</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400" /> Recherche par type d'objet</li>
          </ul>
        </div>

        {/* Écosystème Ajax */}
        <div className="bg-gradient-to-b from-emerald-500/5 to-transparent border border-emerald-500/20 rounded-2xl p-6 relative">
          <div className="absolute top-4 right-4 px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-[10px] font-bold rounded uppercase">
            Synergie
          </div>
          <div className="w-11 h-11 rounded-lg bg-emerald-500/15 flex items-center justify-center mb-4">
            <Shield className="w-5 h-5 text-emerald-300" />
          </div>
          <h3 className="text-white font-semibold mb-2">Couplée à votre centrale + sirène</h3>
          <p className="text-white/60 text-sm mb-4 leading-relaxed">
            Une caméra seule enregistre. Couplée à une centrale et une sirène Ajax, elle <strong className="text-white">vérifie, dissuade et déclenche</strong> en temps réel.
          </p>
          <ul className="space-y-1.5 text-xs text-white/55">
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> Vérification visuelle d'alerte</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> Sirène déclenchée à l'intrusion</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> Notification + vidéo dans l'app</li>
          </ul>
        </div>
      </div>

      {/* Bandeau scenario concret */}
      <div className="bg-zinc-950/50 border border-white/5 rounded-2xl p-6">
        <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-400" />
          Que se passe-t-il en cas d'intrusion ?
        </h4>
        <div className="grid md:grid-cols-4 gap-4">
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-500/20 text-purple-300 flex items-center justify-center text-xs font-bold">1</span>
            <div>
              <p className="text-white text-sm font-medium mb-1">Détection</p>
              <p className="text-white/50 text-xs">Le détecteur de mouvement Ajax repère l'intrus.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center text-xs font-bold">2</span>
            <div>
              <p className="text-white text-sm font-medium mb-1">Vérification IA</p>
              <p className="text-white/50 text-xs">La caméra capture la séquence et confirme la présence d'un humain.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center text-xs font-bold">3</span>
            <div>
              <p className="text-white text-sm font-medium mb-1">Dissuasion</p>
              <p className="text-white/50 text-xs">La sirène hurle, les LED couleur s'allument — l'intrus fuit.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center text-xs font-bold">4</span>
            <div>
              <p className="text-white text-sm font-medium mb-1">Alerte vous</p>
              <p className="text-white/50 text-xs">Vous recevez la vidéo dans l'app Ajax en quelques secondes.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mini CTA */}
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
        <Smartphone className="w-5 h-5 text-white/40" />
        <p className="text-white/50 text-sm">
          Le tout pilotable depuis <strong className="text-white">une seule application</strong>, sur smartphone et bureau.
        </p>
      </div>
    </section>
  );
};

export default VideoEducationSection;
